import { useEffect, useState } from "react";
import { api, initials } from "../lib/api";
import { StageBadge } from "../components/StageBadge";
import { PhysicianDetailSheet } from "../components/PhysicianDetailSheet";
import { AddPhysicianDialog } from "../components/AddPhysicianDialog";
import { ImportCSVDialog } from "../components/ImportCSVDialog";
import { Input } from "../components/ui/input";
import { Buildings, MapPin, Users as UsersIcon, MagnifyingGlass, CaretDown, CaretRight, Envelope, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { ComposeEmailDialog } from "../components/ComposeEmailDialog";

export default function Clinics() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/clinics");
    setClinics(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = clinics.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.city?.toLowerCase().includes(s) || c.state?.toLowerCase().includes(s) || c.zipcode?.includes(s);
  });

  const toggle = (name) => setExpanded({ ...expanded, [name]: !expanded[name] });

  return (
    <div className="p-8" data-testid="clinics-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">Site Accounts</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Clinics</h1>
          <p className="text-sm text-[#5C5E66] mt-2">All physician leads grouped by clinic / practice. Expand a clinic to drill into its physicians. To add a new clinic, add a physician below and set their Practice Name — the clinic appears here automatically.</p>
        </div>
        <div className="flex gap-2">
          <ImportCSVDialog onImported={load} />
          <AddPhysicianDialog onAdded={load} />
        </div>
      </div>

      <div className="relative max-w-md mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5E66]" />
        <Input data-testid="clinics-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clinic name, city, ZIP..." className="rounded-none pl-9" />
      </div>

      <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-2">{filtered.length} {filtered.length === 1 ? "clinic" : "clinics"}</div>

      <div className="bg-white border border-[#E2E2E5]" data-testid="clinics-table">
        <div className="grid grid-cols-[40px_2.5fr_1.5fr_1fr_1fr_120px] gap-0 border-b border-[#E2E2E5] bg-[#F0F0F2]">
          <Th></Th><Th>Clinic</Th><Th>Location</Th><Th>Type</Th><Th>Specialties</Th><Th>Physicians</Th>
        </div>

        {loading ? (
          <div className="p-12 text-center font-mono text-xs uppercase tracking-wider text-[#5C5E66]">LOADING...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[#5C5E66]">
            <Buildings size={36} weight="light" className="mx-auto mb-2" />
            <div className="text-sm">No clinics yet — add physicians with a clinic name to populate this view.</div>
          </div>
        ) : filtered.map((c) => (
          <div key={c.name} className="border-b border-[#E2E2E5] last:border-0" data-testid={`clinic-row-${c.name}`}>
            <div onClick={() => toggle(c.name)} className="grid grid-cols-[40px_2.5fr_1.5fr_1fr_1fr_120px] items-center cursor-pointer row-hover">
              <Td><button className="text-[#5C5E66]">{expanded[c.name] ? <CaretDown size={16} /> : <CaretRight size={16} />}</button></Td>
              <Td>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#FF9900] text-white flex items-center justify-center flex-shrink-0">
                    <Buildings size={18} weight="regular" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    {c.address && <div className="text-[10px] font-mono text-[#5C5E66] truncate">{c.address}</div>}
                  </div>
                </div>
              </Td>
              <Td className="text-sm">{[c.city, c.state].filter(Boolean).join(", ") || "—"} <span className="font-mono text-xs text-[#5C5E66]">{c.zipcode}</span></Td>
              <Td className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">{c.practice_type}</Td>
              <Td className="text-xs">{(c.specialties || []).slice(0, 2).join(", ") || "—"}{(c.specialties || []).length > 2 && <span className="text-[#5C5E66]"> +{c.specialties.length - 2}</span>}</Td>
              <Td>
                <span className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold tabular-nums text-[#006CFF]">
                  <UsersIcon size={14} />{c.physician_count}
                </span>
              </Td>
            </div>

            {expanded[c.name] && (
              <div className="bg-[#F7F7F8] border-t border-[#E2E2E5]">
                {(c.physicians || []).map((p) => (
                  <div
                    key={p.id}
                    data-testid={`clinic-physician-${p.id}`}
                    className="grid grid-cols-[40px_2.5fr_1.5fr_1fr_1fr_120px] items-center row-hover border-b border-[#E2E2E5] last:border-0"
                  >
                    <Td></Td>
                    <Td onClick={() => setSelected(p.id)} className="cursor-pointer">
                      <div className="flex items-center gap-3 pl-4">
                        <div className="w-7 h-7 bg-[#0A0B0F] text-white flex items-center justify-center font-mono font-semibold text-[10px]">{initials(p.full_name)}</div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{p.full_name}</div>
                          <div className="text-[10px] text-[#5C5E66] truncate">{p.specialty || "—"}</div>
                        </div>
                      </div>
                    </Td>
                    <Td className="text-xs">
                      {p.email && <div className="flex items-center gap-1 truncate"><Envelope size={10} className="text-[#5C5E66]" />{p.email}</div>}
                      {p.phone && <div className="flex items-center gap-1 font-mono text-[#5C5E66] truncate"><Phone size={10} />{p.phone}</div>}
                      {!p.email && !p.phone && "—"}
                    </Td>
                    <Td>
                      {p.lead_score ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono tabular-nums" style={{ background: scoreBg(p.lead_score), color: scoreColor(p.lead_score), border: `1px solid ${scoreColor(p.lead_score)}33` }}>
                          {p.lead_score}/100
                        </span>
                      ) : "—"}
                    </Td>
                    <Td><StageBadge stage={p.stage} /></Td>
                    <Td>
                      <button
                        onClick={() => setComposing(p.id)}
                        data-testid={`clinic-compose-${p.id}`}
                        title="Compose email"
                        className="p-1.5 hover:bg-[#006CFF14] text-[#5C5E66] hover:text-[#006CFF]"
                      >
                        <EnvelopeSimple size={14} weight="regular" />
                      </button>
                    </Td>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <PhysicianDetailSheet
        physicianId={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onUpdated={load}
      />

      <ComposeEmailDialog
        physicianId={composing}
        open={!!composing}
        onOpenChange={(o) => !o && setComposing(null)}
      />
    </div>
  );
}

const Th = ({ children }) => <div className="px-4 py-2.5 text-[10px] font-mono uppercase tracking-wider text-[#5C5E66]">{children}</div>;
const Td = ({ children, className = "", onClick }) => <div onClick={onClick} className={`px-4 py-3 ${className}`}>{children}</div>;

const scoreColor = (s) => s >= 75 ? "#2E7D32" : s >= 50 ? "#006CFF" : s >= 30 ? "#FF9900" : "#5C5E66";
const scoreBg = (s) => s >= 75 ? "#2E7D3214" : s >= 50 ? "#006CFF14" : s >= 30 ? "#FF990014" : "#5C5E6614";
